import { useCallback } from "react";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { HiOutlineTrash } from "react-icons/hi2";

import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";

type Props<T> = {
  onClose: (value: boolean) => void;
  preventClose?: boolean;
  selectedRows: Row<T>[];
  onRemoveRows: (rowIds: string[]) => void;
};

const DeleteRowModal = <T,>({ onClose, preventClose = false, selectedRows, onRemoveRows }: Props<T>) => {
  const { t } = useTranslation();

  const firstCompany = selectedRows[0].original as CompanyEntryDto;

  const onDelete = useCallback(() => {
    onRemoveRows(selectedRows.map((row) => row.id));
    onClose(true);
  }, [onRemoveRows, onClose, selectedRows]);

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlineTrash} size={"lg"} color={"error"} />}
      heading={t("search.table.row.delete.deleteCompany", {
        company: firstCompany.info.name,
        count: selectedRows.length - 1,
      })}
      preventClose={preventClose}
      body={t("search.table.row.delete.sure", { count: selectedRows.length })}
      onClose={onClose}
      data-testid={"delete-row-modal"}
    >
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button type={"button"} label={t("cancel")} onClick={() => onClose(false)} color="natural" variants="outline" />
        <Button
          type={"button"}
          label={t("search.table.row.delete.delete", { count: selectedRows.length })}
          color="error"
          variants="primary"
          onClick={onDelete}
        />
      </div>
    </BasicModalContent>
  );
};

export default DeleteRowModal;
